import difference from 'lodash/difference'
import { dequal } from 'dequal'

import type { ConductorTask } from 'Automations/nodeDefinitions/conductor'

/**
 * Takes a set of supporting task branches, extracts the tasks, and combines
 * them into a new set of branches that satisfy all of the task dependencies.
 *
 * This ensure we don't run into duplicate task issues, and that we also don't
 * end up spamming a ton of tasks!
 *
 * The branches this produces are always serial - in the future we may look
 * to optimise this to include forks, but that involves insights into the
 * semantic types and such to be truly robust. Not worth it, for now!
 */
export const mergeSupportingBranches = (
  branches: ConductorTask[][]
): ConductorTask[][] => {
  /**
   * Get the set of unique tasks with their immediate dependencies. We can
   * use this to then build up a new set of graphs containing all of them.
   */
  const resolvedTasks = getTaskDependencies({
    branches,
    allTasks: getAllTasks(branches)
  })
  const optimisedBranches = getNewBranches(resolvedTasks)
  /**
   * Strip away the dependencies, since this is internal metadata that we
   * don't want leaking out.
   */
  return optimisedBranches.map(branch =>
    branch.map(({ dependencies: _dependencies, ...task }) => task)
  )
}

/**
 * A task carrying the immediate preceding dependencies from
 * all branches it appears in, so that
 * we can traverse the graph easily once we've flattened out
 * all the tasks.
 */
type TaskWithDependencies = ConductorTask & {
  dependencies: string[]
}

/**
 * Gets all of the tasks across all branches, de-duping any identical references,
 * and throwing if a reference name is used for two different configs.
 */
const getAllTasks = (branches: ConductorTask[][]): TaskWithDependencies[] => {
  return branches.flat().reduce<TaskWithDependencies[]>((graph, task) => {
    const taskName = task.taskReferenceName
    const existing = getTaskByName({ graph, taskName })

    if (existing) {
      /**
       * Compare definitions only — a task's dependencies are ours, not the
       * caller's, so they must not count towards a clash.
       **/
      const isSameDefinition = dequal(existing, {
        ...task,
        dependencies: existing.dependencies
      })

      if (!isSameDefinition) {
        throw new Error(
          `supporting task "${taskName}" declared twice with different definitions`
        )
      }
      return graph
    }

    return [...graph, { ...task, dependencies: [] }]
  }, [])
}

/**
 * Gets each task's immediate dependencies, which allows us to build
 * up the new graph later, since now we have a bundle of dependencies,
 * seperate from their original branches!
 *
 * We aren't aiming to do anything fancy with semantic types here, yet,
 * we're just doing a more basic 'how can we merge the unique tasks together in
 * a way that works' using connections.
 */
const getTaskDependencies = ({
  branches,
  allTasks
}: {
  branches: ConductorTask[][]
  allTasks: TaskWithDependencies[]
}): TaskWithDependencies[] =>
  branches
    .flatMap(branch =>
      branch.reduce<BranchEdge[]>((edges, task, index) => {
        const previous = index === 0 ? undefined : branch.at(index - 1)
        if (!previous) return edges

        return [
          ...edges,
          { from: previous.taskReferenceName, to: task.taskReferenceName }
        ]
      }, [])
    )
    /**
     * Dedupe any duplicate edges across all branches, so we end up with the
     * final unique set of connections.
     */
    .reduce(
      (graph, edge) =>
        graph.map(entry => {
          const isTarget = entry.taskReferenceName === edge.to
          const alreadyLinked = entry.dependencies.includes(edge.from)
          if (!isTarget || alreadyLinked) return entry

          return { ...entry, dependencies: [...entry.dependencies, edge.from] }
        }),
      allTasks
    )

/**
 * With the set of tasks, create new branches which satisfy their dependencies!
 */
const getNewBranches = (
  graph: TaskWithDependencies[]
): TaskWithDependencies[][] => {
  /** Get the groups of tasks that are part of the new branches */
  const connectedTaskGroups = groupConnectedTasks(graph)
  /** And, then, order those tasks within the branch */
  return connectedTaskGroups.map(taskNames =>
    orderConnectedTasks({
      tasks: graph.filter(task => taskNames.includes(task.taskReferenceName))
    })
  )
}

/**
 * If tasks are connected - directly or otherwise - we'll put them into
 * the same branch. This function finds those connections!
 */
const groupConnectedTasks = (graph: TaskWithDependencies[]): string[][] => {
  const allTaskNames = graph.map(entry => entry.taskReferenceName)

  return allTaskNames.reduce<string[][]>((groups, taskName) => {
    const alreadyPlaced = groups.some(group => group.includes(taskName))

    if (alreadyPlaced) return groups

    const connected = connectedTo({
      graph,
      taskNames: [taskName],
      traversedTasks: []
    })

    return [
      ...groups,
      allTaskNames.filter(candidate => connected.includes(candidate))
    ]
  }, [])
}

const connectedTo = ({
  graph,
  taskNames,
  traversedTasks
}: {
  graph: TaskWithDependencies[]
  taskNames: string[]
  traversedTasks: string[]
}): string[] => {
  /**
   * If we've already seen, and traversed, all of the tasks we are evaluating,
   * then we're done.
   */
  const tasksToTraverse = taskNames.filter(
    taskName => !traversedTasks.includes(taskName)
  )
  if (tasksToTraverse.length === 0) return traversedTasks

  return connectedTo({
    graph,
    taskNames: tasksToTraverse.flatMap(taskName =>
      getAdjacentTasks({ graph, taskName })
    ),
    traversedTasks: [...traversedTasks, ...tasksToTraverse]
  })
}

/**
 * Gets the tasks that are directly connected to the given one, so we
 * can continue to traverse the graph and build our distinct sets of
 * related tasks.
 *
 * By checking both upstream and downstream relationships, it doesn't matter
 * where on the graph we start - we'll always end up mapping it all.
 */
const getAdjacentTasks = ({
  graph,
  taskName
}: {
  graph: TaskWithDependencies[]
  taskName: string
}): string[] => [
  ...(getTaskByName({ graph, taskName })?.dependencies ?? []),
  ...graph
    .filter(entry => entry.dependencies.includes(taskName))
    .map(entry => entry.taskReferenceName)
]

type BranchEdge = { from: string; to: string }

const getTaskByName = ({
  graph,
  taskName
}: {
  graph: TaskWithDependencies[]
  taskName: string
}): TaskWithDependencies | undefined =>
  graph.find(entry => entry.taskReferenceName === taskName)

const orderConnectedTasks = ({
  tasks,
  remaining = tasks,
  emitted = []
}: {
  tasks: TaskWithDependencies[]
  remaining?: TaskWithDependencies[]
  emitted?: TaskWithDependencies[]
}): TaskWithDependencies[] => {
  if (remaining.length === 0) return emitted

  /**
   * If we have added all task dependencies to the emitted list, then
   * we can add the next task to the emitted list.
   */
  const emittedTaskReferenceNames = emitted.map(task => task.taskReferenceName)
  const next = remaining.find(
    task =>
      difference(task.dependencies, emittedTaskReferenceNames).length === 0
  )

  if (!next) {
    const remainingNames = remaining.map(task => task.taskReferenceName)
    throw new Error(
      `supporting tasks form a cycle: ${remainingNames.join(', ')}`
    )
  }

  return orderConnectedTasks({
    tasks,
    remaining: remaining.filter(task => task !== next),
    emitted: [...emitted, next]
  })
}
